import { useState } from "react";
import {
  X, Copy, Check, MessageCircle, Facebook, Phone,
} from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

/**
 * Fallback share modal used when navigator.share is unavailable.
 *
 * IMPORTANT: every channel uses the CANONICAL URL we were given.  We
 * do not append tracking parameters that would break canonicalisation.
 * The `text` we share is the same short, neutral description the
 * native share sheet would have shown: no guarantees, no "verified",
 * no "best".
 */
export default function ShareModal({ url, title, text, onClose, onToast }) {
  const [copied, setCopied] = useState(false);
  const { panelRef } = useAccessibleOverlay(true, onClose);

  const encText = encodeURIComponent(`${text} ${url}`);
  const encUrl  = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title);

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encText}`,
      Icon: WhatsAppGlyph,
      brand: "bg-[#25D366]",
      testId: "share-channel-whatsapp",
    },
    {
      key: "messenger",
      label: "Messenger",
      // Web-based Facebook Share Dialog: works on desktop and mobile,
      // and on mobile most browsers auto-deep-link into the Messenger
      // app when the user taps a contact.
      href: `https://www.facebook.com/dialog/send?link=${encUrl}&app_id=140586622674265&redirect_uri=${encUrl}`,
      Icon: MessengerGlyph,
      brand: "bg-[#0866FF]",
      testId: "share-channel-messenger",
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      Icon: Facebook,
      brand: "bg-[#1877F2]",
      testId: "share-channel-facebook",
    },
    {
      key: "viber",
      label: "Viber",
      href: `viber://forward?text=${encText}`,
      Icon: ViberGlyph,
      brand: "bg-[#7360F2]",
      testId: "share-channel-viber",
    },
    {
      key: "sms",
      label: "SMS",
      href: `sms:?&body=${encText}`,
      Icon: Phone,
      brand: "bg-slate-700",
      testId: "share-channel-sms",
    },
    {
      key: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${encUrl}&text=${encTitle}`,
      Icon: TelegramGlyph,
      brand: "bg-[#229ED9]",
      testId: "share-channel-telegram",
    },
  ];

  async function copyLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      onToast?.("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      onToast?.("Could not copy");
    }
  }

  function openChannel(ch, e) {
    // Anchor `href` already encodes the right URL for every channel
    // (native deep links + web fallbacks). Keep this hook for future
    // analytics; currently a no-op so popup blockers don't interfere.
    void ch; void e;
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="share-backdrop"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 outline-none"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        data-testid="share-modal"
      >
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div>
            <div id="share-modal-title" className="font-display font-semibold text-slate-900">Share this listing</div>
            <div className="text-xs text-slate-500 mt-0.5">Send the link through your favourite app</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Close share dialog"
            data-testid="share-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* URL row */}
        <div className="p-4">
          <div
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
          >
            <input
              readOnly
              value={url}
              data-testid="share-url-input"
              className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 focus:outline-none truncate"
              onFocus={(e) => e.target.select()}
              aria-label="Public link"
            />
            <button
              type="button"
              onClick={copyLink}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold ${
                copied ? "bg-emerald-50 text-emerald-700" : "bg-[#1B4332] text-white hover:bg-[#143027]"
              }`}
              data-testid="share-copy-link-btn"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Channels grid */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            {channels.map((ch) => (
              <a
                key={ch.key}
                href={ch.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => openChannel(ch, e)}
                data-testid={ch.testId}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-center"
              >
                <span className={`w-11 h-11 rounded-full text-white grid place-items-center ${ch.brand}`}>
                  <ch.Icon className="w-5 h-5" />
                </span>
                <span className="text-xs font-medium text-slate-700">{ch.label}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="px-4 pb-4 text-[11px] text-slate-400 leading-relaxed">
          ConfirmPH lists rental businesses; we don&apos;t make safety or trust
          guarantees on their behalf.
        </div>
      </div>
    </div>
  );
}

/* ----- Brand glyphs as inline SVGs (kept tiny on purpose) -------------- */

function WhatsAppGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.04 2.4a9.6 9.6 0 0 0-8.3 14.4L2.4 21.6l4.96-1.3a9.6 9.6 0 1 0 4.68-17.9zm5.62 13.6c-.24.68-1.4 1.32-1.92 1.4-.5.08-1.1.1-1.78-.12-.42-.14-.96-.32-1.66-.62-2.9-1.26-4.8-4.18-4.94-4.38-.14-.2-1.18-1.56-1.18-2.98 0-1.42.74-2.12 1-2.4.26-.28.58-.36.78-.36h.56c.18 0 .42-.06.66.5.24.58.82 2 .9 2.16.08.16.12.34 0 .54-.1.18-.16.28-.32.46-.16.18-.34.4-.5.54-.16.16-.34.34-.14.66.18.34.84 1.38 1.8 2.24 1.24 1.1 2.28 1.44 2.6 1.6.32.16.5.14.7-.08.18-.2.78-.92.98-1.24.2-.32.4-.26.66-.16.28.1 1.7.8 2 .96.3.16.5.24.58.36.06.14.06.78-.18 1.46z"/>
    </svg>
  );
}
function MessengerGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2C6.48 2 2 6.18 2 11.4c0 2.96 1.46 5.6 3.74 7.34V22l3.4-1.88c.92.26 1.88.4 2.86.4 5.52 0 10-4.18 10-9.4S17.52 2 12 2zm1.04 12.6L10.5 12 5.7 14.6l5.28-5.6 2.6 2.6 4.72-2.6-5.26 5.6z"/>
    </svg>
  );
}
function ViberGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M11.4 16.7c-1-.06-3.4-.36-5.3-2.2-1.86-1.84-2.18-4.16-2.24-5.16-.04-.66.5-1.16.86-1.26l1.2-.34c.28-.08.62.1.74.36l.62 1.36c.12.26.06.56-.14.74l-.34.34c-.18.18-.2.42-.08.62.34.6 1 1.5 1.78 2.28.76.76 1.64 1.4 2.24 1.74.2.12.44.1.62-.08l.34-.34c.18-.2.48-.26.74-.14l1.36.62c.26.12.44.46.36.74l-.34 1.2c-.1.36-.6.9-1.26.86zM12 1.2c-5.96 0-10.8 4.84-10.8 10.8 0 2.06.58 3.98 1.58 5.62L1.2 22.8l5.36-1.4A10.74 10.74 0 0 0 12 22.8c5.96 0 10.8-4.84 10.8-10.8S17.96 1.2 12 1.2z"/>
    </svg>
  );
}
function TelegramGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M21.94 4.06 18.7 19.4c-.24 1.1-.92 1.36-1.86.84l-5.12-3.78-2.46 2.38c-.28.28-.5.5-1.02.5l.36-5.16 9.4-8.48c.4-.36-.1-.56-.62-.2L6.06 12.62l-5-1.56c-1.08-.34-1.1-1.08.22-1.6L20.5 2.78c.9-.34 1.7.22 1.44 1.28z"/>
    </svg>
  );
}
